import { Dayjs } from 'dayjs';
import { Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ShiftType } from '@libs/models/shifts';
import { getShiftType } from '@libs/models/shifts/shift';
import { ShiftLength, TimeRange } from '@molecules/dataDisplay';
import { Box, Flex, type FlexProps } from '@atoms/layout';
import { ArchivedShiftIcon, FaIcon, HotShiftIcon, RotationIcon, SeriesIcon, SharedShiftIcon } from '@atoms/icons';

type ShiftTypeProps = {
  startTime: Dayjs;
  endTime: Dayjs;
  disableShiftType?: boolean;
  boxProps?: FlexProps;
  timeDisplay?: 'full' | 'short';
  isArchive?: boolean;
  isUrgent?: boolean;
  isShared?: boolean;
  isSeries?: boolean;
  isRotation?: boolean;
  isMultipleAssignees?: boolean;
};
// TODO: probably the name is not very good. We add a lot of different data here.
// Probably we should rename it to `ShiftPeriod` or something like that
export function ShiftType({
  startTime,
  endTime,
  boxProps,
  disableShiftType,
  timeDisplay = 'full',
  isArchive,
  isRotation,
  isSeries,
  isShared,
  isUrgent,
  isMultipleAssignees,
}: ShiftTypeProps) {
  const length = endTime.diff(startTime);
  const theme = useTheme();
  const shouldShowaAssigneesType = isRotation || isSeries;
  const isMultiple = shouldShowaAssigneesType && isMultipleAssignees;
  const isSingle = shouldShowaAssigneesType && !isMultipleAssignees;

  return (
    <Flex alignCenter gap={1} {...boxProps}>
      {!disableShiftType && <ShiftTypeIndicator startTime={startTime} />}
      <Flex column maxWidth="100%">
        <Typography variant="body2" noWrap>
          <ShiftRange startTime={startTime} length={length} timeDisplay={timeDisplay} />
        </Typography>

        <Flex gap={1} alignCenter>
          <Typography variant="caption" color="text.secondary" lineHeight={1.5}>
            <ShiftLength startTime={startTime} length={length} />
          </Typography>
          {isRotation && (
            <Tooltip title="Rotation" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.rotation.main}>
                <RotationIcon />
              </Flex>
            </Tooltip>
          )}
          {isSeries && (
            <Tooltip title="Series" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main}>
                <SeriesIcon />
              </Flex>
            </Tooltip>
          )}
          {isUrgent && (
            <Tooltip title="Urgent" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main}>
                <HotShiftIcon />
              </Flex>
            </Tooltip>
          )}
          {isShared && (
            <Tooltip title="Shared" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main}>
                <SharedShiftIcon />
              </Flex>
            </Tooltip>
          )}
          {isArchive && (
            <Tooltip title="Archived" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main} width={13} height={13}>
                <ArchivedShiftIcon />
              </Flex>
            </Tooltip>
          )}
          {isSingle && (
            <Tooltip title="Single Assignee" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main} width={13} height={15}>
                <FaIcon
                  variant="duotone"
                  name="user"
                  height={'10px'}
                  width={'10px'}
                  color={theme.palette.primary.main}
                />
              </Flex>
            </Tooltip>
          )}
          {isMultiple && (
            <Tooltip title="Multiple Assignees" placement="top">
              <Flex color={(theme) => theme.palette.shiftRecurrence.series.main} width={13} height={15}>
                <FaIcon
                  variant="duotone"
                  name="users"
                  height={'15px'}
                  width={'15px'}
                  color={theme.palette.primary.main}
                />
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
export function ShiftTypeInline({ startTime, endTime, boxProps, timeDisplay = 'full' }: ShiftTypeProps) {
  const length = endTime.diff(startTime);
  return (
    <Flex alignCenter gap={1} {...boxProps}>
      <ShiftTypeIndicator startTime={startTime} />
      <Flex maxWidth="100%">
        <Typography variant="body2" noWrap>
          <ShiftRange startTime={startTime} length={length} timeDisplay={timeDisplay} />
          {', '}
          <ShiftLength startTime={startTime} length={length} />
        </Typography>
      </Flex>
    </Flex>
  );
}

type ShiftTypeIndicatorProps = {
  startTime: Dayjs;
};
export function ShiftTypeIndicator({ startTime }: ShiftTypeIndicatorProps) {
  const theme = useTheme();
  const shiftType = getShiftType(startTime);
  return <Box width={10} height={10} bgcolor={theme.palette.shiftType[shiftType]} borderRadius="50%" flexShrink={0} />;
}

type ShiftRangeProps = {
  startTime: Dayjs;
  length: number;
  timeDisplay: 'full' | 'short';
};
function ShiftRange({ startTime, length, timeDisplay = 'full' }: ShiftRangeProps) {
  const to = startTime.add(length, 'millisecond');
  return <TimeRange from={startTime} to={to} timeDisplay={timeDisplay} />;
}
